<script setup>
import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';
import Helmet from '@/components/Helmet.vue';
import { translate } from "../assets/translations.js";
import { useRouter, useRoute, onBeforeRouteUpdate, nextTick } from 'vue-router';
import { onMounted } from 'vue';

const router = useRouter();
const route = useRoute();

async function scrollToContact() {
  if (route.path === '/') {
    // Ya estamos en la página principal, solo hacer scroll suave
    await nextTick(); // Espera a que el DOM esté actualizado
    scrollToSection();
  } else {
    // Navega a '/' con hash #contact para que la URL sea correcta y el scroll se haga en la navegación
    router.push({ path: '/', hash: '#contact' });
  }
}

function scrollToSection() {
  const contactSection = document.getElementById('contact');
  if (contactSection) {
    contactSection.scrollIntoView({ behavior: 'smooth' });
  } else {
    // Si la sección no está aún en el DOM, observar cambios y hacer scroll cuando aparezca
    const observer = new MutationObserver(() => {
      const section = document.getElementById('contact');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

// Cuando el componente monta, si la URL tiene #contact, hacer scroll
onMounted(async () => {
  if (route.hash === '#contact') {
    await nextTick();
    scrollToSection();
  }
});

// Cuando cambia la ruta, si el destino tiene hash #contact, hacer scroll
onBeforeRouteUpdate(async (to) => {
  if (to.hash === '#contact') {
    await nextTick();
    scrollToSection();
  }
});
</script>



<template>
  <Helmet title="Reparación de Celulares y PCs, Asesoría Legal y Edición de Video | Resi Corporation" />
  <Header />

  <section class="associates-container">
    <h2 class="title">{{ translate("associates").name }}</h2>
    <p class="description">
      {{ translate("associates").content }}
    </p>

    <div class="services-grid">

        <div class="service-card">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="currentColor"><path d="M4 16H20V5H4V16ZM13 18V20H17V22H7V20H11V18H2.9918C2.44405 18 2 17.5511 2 16.9925V4.00748C2 3.45107 2.45531 3 2.9918 3H21.0082C21.556 3 22 3.44892 22 4.00748V16.9925C22 17.5489 21.5447 18 21.0082 18H13Z"></path></svg>
        <h2>{{ translate("associates").card1t }}</h2>
        <p>
          {{ translate("associates").card1d }}
        </p>
        <button @click="scrollToContact" aria-label="Solicitar este servicio">
          {{ translate("associates").btn }}
        </button>

      </div>

      <div class="service-card">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="currentColor"><path d="M12.9985 2L12.9979 3.278L17.9985 4.94591L21.631 3.73509L22.2634 5.63246L19.2319 6.643L22.3272 15.1549C21.2353 16.2921 19.6996 17 17.9985 17C16.2975 17 14.7618 16.2921 13.6699 15.1549L16.7639 6.643L12.9979 5.387V19H16.9985V21H6.99854V19H10.9979V5.387L7.23192 6.643L10.3272 15.1549C9.23528 16.2921 7.69957 17 5.99854 17C4.2975 17 2.76179 16.2921 1.66992 15.1549L4.76392 6.643L1.73363 5.63246L2.36608 3.73509L5.99854 4.94591L10.9979 3.278L10.9985 2H12.9985ZM17.9985 9.10267L16.04 14.4892C16.628 14.8201 17.2979 15 17.9985 15C18.6992 15 19.3691 14.8201 19.957 14.4892L17.9985 9.10267ZM5.99854 9.10267L4.04004 14.4892C4.62795 14.8201 5.29792 15 5.99854 15C6.69916 15 7.36912 14.8201 7.95703 14.4892L5.99854 9.10267Z"></path></svg>
        <h2>{{ translate("associates").card2t }}</h2>
        <p>
          {{ translate("associates").card1d }}
        </p>
        <button @click="scrollToContact" aria-label="Solicitar este servicio">
         {{ translate("associates").btn }}
        </button>

      </div>

      <div class="service-card">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="currentColor"><path d="M12 2C17.5222 2 22 5.97778 22 10.8889C22 13.9556 19.5111 16.4444 16.4444 16.4444H14.4778C13.5556 16.4444 12.8111 17.1889 12.8111 18.1111C12.8111 18.5333 12.9778 18.9222 13.2333 19.2111C13.5 19.5111 13.6667 19.9 13.6667 20.3333C13.6667 21.2556 12.9 22 12 22C6.47778 22 2 17.5222 2 12C2 6.47778 6.47778 2 12 2ZM10.8111 18.1111C10.8111 16.0843 12.451 14.4444 14.4778 14.4444H16.4444C18.4065 14.4444 20 12.851 20 10.8889C20 7.1392 16.4677 4 12 4C7.58235 4 4 7.58235 4 12C4 16.19 7.2226 19.6285 11.324 19.9718C10.9948 19.4168 10.8111 18.7761 10.8111 18.1111ZM7.5 12C6.67157 12 6 11.3284 6 10.5C6 9.67157 6.67157 9 7.5 9C8.32843 9 9 9.67157 9 10.5C9 11.3284 8.32843 12 7.5 12ZM16.5 12C15.6716 12 15 11.3284 15 10.5C15 9.67157 15.6716 9 16.5 9C17.3284 9 18 9.67157 18 10.5C18 11.3284 17.3284 12 16.5 12ZM12 9C11.1716 9 10.5 8.32843 10.5 7.5C10.5 6.67157 11.1716 6 12 6C12.8284 6 13.5 6.67157 13.5 7.5C13.5 8.32843 12.8284 9 12 9Z"></path></svg>
        <h2>{{ translate("associates").card3t }}</h2>
        <p>
          {{ translate("associates").card3d }}
        </p>
        <button @click="scrollToContact" aria-label="Solicitar este servicio">
          {{ translate("associates").btn }}
        </button>

      </div>

      <div class="service-card">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="currentColor"><path d="M17 9.2L22.2133 5.55071C22.4395 5.39235 22.7513 5.44737 22.9096 5.6736C22.9684 5.75764 23 5.85774 23 5.96033V18.0397C23 18.3158 22.7761 18.5397 22.5 18.5397C22.3974 18.5397 22.2973 18.5081 22.2133 18.4493L17 14.8V19C17 19.5523 16.5523 20 16 20H2C1.44772 20 1 19.5523 1 19V5C1 4.44772 1.44772 4 2 4H16C16.5523 4 17 4.44772 17 5V9.2ZM17 12.3587L21 15.1587V8.84131L17 11.6413V12.3587ZM3 6V18H15V6H3ZM5 8H7V10H5V8Z"></path></svg>
        <h2>{{ translate("associates").card4t }}</h2>
        <p>
          {{ translate("associates").card4d }}
        </p>
        <button @click="scrollToContact" aria-label="Solicitar este servicio">
          {{ translate("associates").btn }}
        </button>

      </div>

    </div>
  </section>

  <Footer />
</template>

<style>
.associates-container {
  margin-top: 100px !important;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1rem;
  text-align: center;
}

.title {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.description {
  font-size: 1.125rem;
  margin-bottom: 3rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.service-card {
  background-color: var(--body-color);
  border: 1px solid #ccc;
  border-radius: 12px;
  padding: 2rem;
  transition: transform 0.2s ease;
}

.service-card:hover {
  transform: translateY(-5px);
}

.service-card h2 {
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.service-card p {
  font-size: 0.95rem;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.service-card button {
  background-color: transparent;
  border: 1px solid currentColor;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: background 0.2s ease;
  color: var(--title-color);
}

.service-card button:hover {
  background-color: var(--first-color);
}

</style>
